/**
 * Shake Detection Module (TypeScript)
 * Handles mobile device shake detection for rolling dice
 * 
 * Key optimization: Shake capability is determined once at initialization
 * and cached, avoiding redundant checks on every button text update.
 */

import { logger } from '../../lib/logger';
import { showToast } from '../../lib/toast';
import { 
  isMobile, 
  hasMotionSupport, 
  getMotionPermissionState, 
  setMotionPermissionState,
  requestMotionPermission 
} from '../../lib/device-utils';
import { ShakeDetector } from '../../lib/shake-detector';

// =============================================================================
// Cached Shake Capability
// =============================================================================

/**
 * Cached shake capability state.
 * Determined once at initialization to avoid repeated checks.
 */
let shakeCapabilityCache: boolean | null = null;

/**
 * Determines if shake is available based on device and permissions.
 * This is the "source of truth" that caches the result.
 */
function determineShakeCapability(): boolean {
  return isMobile() && hasMotionSupport() && getMotionPermissionState() === 'granted';
}

/**
 * Gets cached shake capability, computing it if needed.
 */
function isShakeCapable(): boolean {
  if (shakeCapabilityCache === null) {
    shakeCapabilityCache = determineShakeCapability();
  }
  return shakeCapabilityCache;
}

/**
 * Updates the shake capability cache.
 * Call this when permission state changes.
 */
function updateShakeCapabilityCache(): void {
  shakeCapabilityCache = determineShakeCapability();
}

/**
 * Parameters for roll button text update
 */
interface UpdateRollButtonParams {
  rollBtn: HTMLElement | null;
}

/**
 * Parameters for motion permission prompts
 */
interface MotionPromptParams {
  motionPromptEl: HTMLElement | null;
}

/**
 * Parameters for enabling shake detection
 */
interface EnableShakeParams extends MotionPromptParams {
  onPermissionGranted: () => void;
  rollBtn: HTMLElement | null;
}

/**
 * Parameters for shake event callbacks
 */
interface ShakeCallbackParams {
  onShakeStart: () => void;
  onShakeActive: (duration: number) => void;
  onShakeEnd: (duration: number) => void;
}

/**
 * Parameters for shake detection initialization
 */
interface InitShakeParams extends ShakeCallbackParams {
  onPermissionGranted: () => void;
  rollBtn: HTMLElement | null;
  motionPromptEl: HTMLElement | null;
}

/**
 * Shake detection result
 */
interface ShakeDetectionResult {
  shakeDetector: ShakeDetector | null;
  cleanup: () => void;
}

/**
 * Updates roll button text based on cached shake capability.
 * Uses the cached value for efficiency - no repeated device/permission checks.
 */
export function updateRollButtonText({ rollBtn }: UpdateRollButtonParams): void {
  if (!rollBtn) return;
  
  const rollBtnText = rollBtn.querySelector('span');
  if (!rollBtnText) return;
  
  // Use cached capability - fast, no repeated checks
  rollBtnText.textContent = isShakeCapable() ? 'Roll / Shake' : 'Roll';
}

/**
 * Shows inline permission prompt for motion access
 */
export function showMotionPermissionPrompt({ motionPromptEl }: MotionPromptParams): void {
  if (motionPromptEl) {
    motionPromptEl.style.display = 'block';
  }
}

/**
 * Hides motion permission prompt
 */
export function hideMotionPermissionPrompt({ motionPromptEl }: MotionPromptParams): void {
  if (motionPromptEl) {
    motionPromptEl.style.display = 'none';
  }
}

/**
 * Handle user enabling shake detection
 */
export async function handleEnableShake({ 
  onPermissionGranted, 
  motionPromptEl, 
  rollBtn 
}: EnableShakeParams): Promise<void> {
  try {
    const permission = await requestMotionPermission();
    // Save permission result to localStorage
    setMotionPermissionState(permission);
    
    // Update cached capability after permission change
    updateShakeCapabilityCache();
    
    if (permission === 'granted') {
      onPermissionGranted();
      updateRollButtonText({ rollBtn });
      hideMotionPermissionPrompt({ motionPromptEl });
      showToast('success', 'Shake to roll enabled!');
    } else {
      hideMotionPermissionPrompt({ motionPromptEl });
      showToast('info', 'Motion permission denied. You can still use the Roll button.');
    }
  } catch (err) {
    showToast('danger', 'Couldn\u2019t enable shake-to-roll — use the Roll button instead');
  }
}

/**
 * Handle user declining shake detection
 */
export function handleDeclineShake({ motionPromptEl }: MotionPromptParams): void {
  setMotionPermissionState('denied');
  // Update cached capability after permission change
  updateShakeCapabilityCache();
  hideMotionPermissionPrompt({ motionPromptEl });
  showToast('info', 'You can still use the Roll button to play');
}

/**
 * Starts the shake detector with event listeners
 */
export function startShakeDetector({ 
  onShakeStart, 
  onShakeActive, 
  onShakeEnd 
}: ShakeCallbackParams): ShakeDetector {
  logger.log('Initializing ShakeDetector...');
  
  const shakeDetector = new ShakeDetector({
    threshold: 18,
    minShakes: 2,
    timeWindow: 600,
    cooldown: 300
  });
  logger.log('ShakeDetector created');
  
  // Listen for shake events
  shakeDetector.addEventListener('shakeStart', () => {
    logger.log('Shake start event');
    onShakeStart();
  });
  
  shakeDetector.addEventListener('shakeActive', (event: Event) => {
    const customEvent = event as CustomEvent<{ duration: number }>;
    logger.log('Shake active:', customEvent.detail.duration, 'ms');
    onShakeActive(customEvent.detail.duration);
  });
  
  shakeDetector.addEventListener('shakeEnd', (event: Event) => {
    const customEvent = event as CustomEvent<{ duration: number }>;
    logger.log('Shake end:', customEvent.detail.duration, 'ms');
    onShakeEnd(customEvent.detail.duration);
  });
  
  logger.log('Event listeners attached');
  
  shakeDetector.start();
  logger.log('ShakeDetector started');
  
  return shakeDetector;
}

/**
 * Initialize shake detection on mobile devices with motion support.
 * Caches the shake capability at initialization for efficient subsequent checks.
 */
export function initShakeDetection({
  onPermissionGranted,
  onShakeStart,
  onShakeActive,
  onShakeEnd,
  rollBtn,
  motionPromptEl
}: InitShakeParams): ShakeDetectionResult {
  // Only enable on mobile devices with motion support
  if (!isMobile() || !hasMotionSupport()) {
    logger.log('Shake detection not available (not mobile or no motion support)');
    // Cache that shake is not capable
    shakeCapabilityCache = false;
    return { shakeDetector: null, cleanup: () => {} };
  }
  
  // Check permission state
  const permissionState = getMotionPermissionState();
  
  // Initialize the cache based on current permission state
  updateShakeCapabilityCache();
  
  // Update button text using cached capability
  updateRollButtonText({ rollBtn });
  
  let shakeDetector: ShakeDetector | null = null;
  
  if (permissionState === 'granted') {
    // Permission already granted, start detector
    shakeDetector = startShakeDetector({ onShakeStart, onShakeActive, onShakeEnd });
  } else if (permissionState === 'prompt') {
    // Show inline prompt on first game interaction
    showMotionPermissionPrompt({ motionPromptEl });
  }
  // If denied, don't show prompt or start detector
  
  // Cleanup function
  const cleanup = () => {
    if (shakeDetector) {
      shakeDetector.stop();
      logger.log('ShakeDetector stopped');
    }
  };
  
  return { shakeDetector, cleanup };
}
